/* eslint-disable max-lines */
import {
  Card,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Title,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Stack, ThemeProvider } from "@mui/material";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { ACCOUNTS_BASE_PATH } from "@src/appV2/redesign/Accounts/paths";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useUpdateWorkerCache } from "@src/appV2/Worker/useUpdateWorkerCache";
import { USER_EVENTS } from "@src/constants";
import { type ReactElement, useMemo, useRef } from "react";

import { ListLoadingState } from "../components/ListLoadingState";
import { PageWrapper } from "../components/PageWrapper";
import { StickyScrollPageHeader } from "../components/StickyScrollPageHeader";
import { PageContent } from "../WorkerShifts/components/PageContent";
import { useGetNotificationPreferences } from "./api/useGetNotifcationPreferences";
import { useSetNotificationPreferences } from "./api/useSetNotificationPreferences";
import { PreferenceItem } from "./PreferenceItem";
import { type PreferenceOnUpdateParams, ResponseCodes } from "./types";

export function NotificationPreferencePage(): ReactElement {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const updateWorkerCache = useUpdateWorkerCache();
  const worker = useDefinedWorker();

  const { showErrorToast } = useToast();

  const {
    data: notificationPreferencesData,
    refetch: notificationPreferencesRefetch,
    isLoading: isLoadingGet,
  } = useGetNotificationPreferences();
  const preferences = useMemo(() => {
    if (
      notificationPreferencesData &&
      typeof notificationPreferencesData.data !== "string" &&
      notificationPreferencesData.data
    ) {
      updateWorkerCache({
        notificationPreferences: notificationPreferencesData.data.notificationPreferences,
      });
      return notificationPreferencesData.data.notificationPreferences;
    }

    return undefined;
  }, [notificationPreferencesData, updateWorkerCache]);

  const { mutateAsync: setNotificationPreference, isLoading: isLoadingSet } =
    useSetNotificationPreferences();

  const isLoading = isLoadingGet || isLoadingSet;

  function logUpdate(newValue: PreferenceOnUpdateParams) {
    const { key, value } = newValue;

    const workerId = worker.userId;

    if (key === "off") {
      const offMeta = {
        makeEnabled: !value,
        workerId,
      };

      logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_CHANNEL_UPDATE, {
        channelName: "push",
        ...offMeta,
      });
      logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_CHANNEL_UPDATE, {
        channelName: "sms",
        ...offMeta,
      });

      return;
    }

    const meta = {
      makeEnabled: value,
      workerId,
    };

    if (["push", "sms"].includes(key)) {
      logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_CHANNEL_UPDATE, {
        channelName: key,
        ...meta,
      });
      return;
    }

    logEvent(USER_EVENTS.NOTIFICATION_PREFERENCE_UPDATE, {
      preferenceName: key,
      ...meta,
    });
  }

  /**
   * Updates preference and updates preference & agent store states
   * @param newValue of type PreferenceOnUpdateParams
   */
  const updatePreference = async (newValue: PreferenceOnUpdateParams) => {
    try {
      const response = await setNotificationPreference({
        notificationPreference: { ...newValue },
      });
      if (response.code === ResponseCodes.SUCCESS) {
        logUpdate(newValue);
        await notificationPreferencesRefetch();
      }
    } catch {
      showErrorToast("Could not change notifications, please try again later");
    }
  };

  /**
   * Constructs props for all Preference Items
   */
  const createPreferenceProps = () => {
    return [
      {
        name: "Push Notification",
        preferenceKey: "push",
        testId: "pushToggle",
        isBusy: isLoading,
        initialValue: preferences?.push,
        confirmAlertProps: {
          header: `"Clipboard" \n would like to send you`,
          subHeader: `Notifications may include alerts, sounds, icons badges. These can be configured in settings`,
          buttonProps: {
            positiveName: `Allow`,
            negativeName: `Don't Allow`,
          },
        },
        onUpdate: updatePreference,
      },
      {
        name: "SMS",
        preferenceKey: "sms",
        testId: "smsToggle",
        isBusy: isLoading,
        initialValue: preferences?.sms,
        confirmAlertProps: {
          header: "Sign up for SMS Shift Alerts ?",
          message: `These notifications help you to stay on top of shifts for our facilities. Message and data rates may apply. Text HELP for help. Text STOP to cancel. Msg freq varies. For healthcare professional terms:
          <a href='https://clipboardhealth.com/professional-terms'>
          https://clipboardhealth.com/professional-terms</a>`,
          buttonProps: {
            positiveName: "Yes",
            negativeName: "No",
          },
        },
        onUpdate: updatePreference,
      },
      {
        name: "Off",
        preferenceKey: "off",
        testId: "offToggle",
        isBusy: isLoading,
        initialValue: preferences?.off,
        confirmAlertProps: {
          header: "Are you sure?",
          subHeader: `If you turn notification off then we won't be able to provide you important updates about your shifts`,
          buttonProps: {
            positiveName: `Yes, I'm sure`,
            negativeName: `No, I'm not sure`,
          },
        },
        onUpdate: updatePreference,
      },
    ];
  };

  /**
   * Constructs Props for urgentShiftsNotification
   */
  const UrgentShiftsNotificationProps = {
    name: "Urgent Shift Notifications",
    caption: "Be notified when high-earning Urgent Shifts become available to book.",
    preferenceKey: "urgentShiftNotification",
    testId: "urgentShiftNotification",
    isBusy: isLoading,
    initialValue: preferences?.urgentShiftNotification?.isOn,
    confirmAlertProps: {
      header: `"Clipboard" \n would like to send you`,
      subHeader: `Urgent Shift Notifications may include alerts, sounds, icons badges.`,
      buttonProps: {
        positiveName: `Allow`,
        negativeName: `Don't Allow`,
      },
    },
    onUpdate: updatePreference,
  };

  /**
   * Constructs Props for PriorityAccessNotifications
   */
  const PriorityAccessNotificationProps = {
    name: "Priority Access Notifications",
    preferenceKey: "priorityAccessPush",
    testId: "priorityAccessPush",
    isBusy: isLoading,
    initialValue: preferences?.priorityAccessPush,
    caption:
      "Receive updates when you get early access to shifts for your solid track record on the platform.",

    confirmAlertProps: {
      header: `"Clipboard" \n would like to send you`,
      subHeader: "Notifications about Priority Access shifts that you have early access to.",
      buttonProps: {
        positiveName: `Allow`,
        negativeName: `Don't Allow`,
      },
    },
    onUpdate: updatePreference,
  };

  /**
   * Constructs Props for PlacementsMarketingNotifications
   */
  const PlacementsMarketingNotificationProps = {
    name: "Clipboard Hiring Notifications",
    caption: "Receive notifications when new permanent job opportunities are posted in your area.",
    preferenceKey: "placementsMarketingPush",
    testId: "placementsMarketingPush",
    isBusy: isLoading,
    initialValue: preferences?.placementsMarketingPush,
    confirmAlertProps: {
      header: `"Clipboard" \n would like to send you`,
      subHeader: "Notifications about permanent job postings in your area.",
      buttonProps: {
        positiveName: `Allow`,
        negativeName: `Don't Allow`,
      },
    },
    onUpdate: updatePreference,
  };

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          shouldWrapTitle
          title="Notification Preferences"
          scrollContainerRef={scrollContainerRef}
          headerVariant="tertiary"
          fallbackGoBackPath={ACCOUNTS_BASE_PATH}
        />
        <PageContent ref={scrollContainerRef}>
          <Card outlined variant="primary" sx={{ width: "100%", padding: 5 }}>
            <Stack spacing={5}>
              <Title variant="h5" component="h5">
                Account and shifts updates
              </Title>
              {createPreferenceProps().map((preferenceProp) => {
                return <PreferenceItem key={preferenceProp.preferenceKey} {...preferenceProp} />;
              })}
            </Stack>
          </Card>
          <List
            component="div"
            sx={{
              "& li": {
                height: 62,
              },
            }}
          >
            <ListItem>
              <ListItemIcon iconType="bolt" />
              <ListItemText>
                <PreferenceItem {...UrgentShiftsNotificationProps} />
              </ListItemText>
            </ListItem>
            <Divider />

            <ListItem>
              <ListItemIcon iconType="lock-with-star-violet" />
              <ListItemText>
                <PreferenceItem {...PriorityAccessNotificationProps} />
              </ListItemText>
            </ListItem>
            <Divider />

            <ListItem>
              <ListItemIcon iconType="briefcase" />
              <ListItemText>
                <PreferenceItem {...PlacementsMarketingNotificationProps} />
              </ListItemText>
            </ListItem>
            <Divider />

            {isLoading && <ListLoadingState texts={[""]} />}
          </List>
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}

/* eslint-enable max-lines */
